import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import MaterialIcon from './MaterialIcon';
import { trackStickerEngagement } from '../services/analyticsService';
import logger from '../utils/logger.js';
import './HomepageStickerCard.css';

const HomepageStickerCard = React.memo(({
  listingId,
  title,
  category,
  svgPath,
  firstImageUrl,
  largestSizeImageUrl,
  etsyUrl,
  onCardClick,
  onAddToScene,
  // Debug props for sales/views overlay
  totalSalesVolume,
  views,
  showDebugOverlay = false,
  debugType = 'sales', // 'sales' or 'views'
  // Pack data
  svgPaths = [],
  svgTitles = [],
		isSet = false,
  packType = null,
		setQuantity = null,
  // TRUEscale badge
  isTrueScale = false,
  // Priority loading for deep links
  isPriorityLoad = false,
  // Modal state - pause loading when modal is open AND its image hasn't loaded yet
  isModalOpen = false,
  modalImageLoaded = false
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isInView, setIsInView] = useState(false);
  
  const cardRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Log priority loading
  if (isPriorityLoad) {
    logger.log(`🎯 PRIORITY IMAGE LOAD: ${title} (${listingId})`);
  }

  // Handle image loading
  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
    // Track image load success
          // Removed image_load_success tracking as requested
  }, [listingId, category]);

  const handleImageError = useCallback(() => {
    setImageError(true);
    // Track image load failure
          // Removed image_load_error tracking as requested
  }, [listingId, category]);

  // Get category collection fallback image
  const getCategoryFallbackImage = useCallback(() => {
    if (category) {
      // Strip " Stickers" suffix from category name first
      const cleanCategory = category.replace(/\s+stickers?$/i, '').trim();

      // Map category names to collection file names
      const categoryMappings = {
        'cryptids': 'cryptid',
        'cryptid': 'cryptid', 
        'dinosaurs': 'dino',
        'dinosaur': 'dino',
        'dino': 'dino',
        'animals': 'animals',
        'animal': 'animals',
        'cartoons': 'cartoon',
        'cartoon': 'cartoon',
        'vehicles': 'vehicles',
        'vehicle': 'vehicles',
        'space': 'space',
        'military': 'military',
        'police/military/fire': 'military',
        'horror': 'horror',
        'horror classic': 'horror-classic',
        'holiday': 'holiday',
        'pop culture': 'popculture',
        'popculture': 'popculture',
        'vehicles pop culture': 'vehicles',
        'outdoor': 'outdoor',
        'offroad': 'offroad',
        'offroad slogans': 'offroad-slogans'
      };
      
      const categoryLower = cleanCategory.toLowerCase();
      const collectionName = categoryMappings[categoryLower] || categoryLower.replace(/[\/\s]+/g, '-');
      return `/assets/images/collection-${collectionName}.png`;
    }
    return '/assets/svgs/clothedEye-watermark.svg';
  }, [category]);

  // Handle card click
  const handleClick = useCallback((e) => {
    e.stopPropagation();
    // Track card click
    trackStickerEngagement(listingId, 'card_click', category, null, title);
    if (onCardClick) onCardClick();
  }, [listingId, category, onCardClick, title]);

  // Handle add to scene
  const handleAddToScene = useCallback(() => {
    logger.log('🎭 HomepageStickerCard handleAddToScene called with:', {
              isSet,
      svgPath,
      svgPaths,
      svgTitles,
      title
    });
    
    // Track add to scene action
    trackStickerEngagement(listingId, 'add_sticker_to_scene', category, 'N/A', title);
    
            if (isSet && (svgPath || (svgPaths && svgPaths.length > 0))) {
      // Handle SET pack (multiple different designs)
      const packStickers = [];
      
      // Add main design (Design 1) using main svgPath
      if (svgPath) {
        logger.log('🎭 Adding main design with svgPath:', svgPath);
        packStickers.push({
          listingId,
          title: `${title} - Main Design`,
          size: 'MICRO',
          color: 'Black',
          image: svgPath,
          etsyUrl,
          svgPath: svgPath,
          // Pass through pack data - these will be populated by SceneBuilder when it fetches unified data
          availableSizes: [],
          availableColors: [],
          dimensions: {},
          // 🎯 CRITICAL: Use the ACTUAL Etsy data fields that exist
          listing_data: null,
          inventory_data: null,
          category,
          isSet,
          setQuantity,
          svgPaths,
          svgTitles
        });
      }
      
      // Add additional designs (Design 2, 3, etc.) using svgPaths
      if (svgPaths && svgPaths.length > 0) {
        logger.log('🎭 Adding additional designs with svgPaths:', svgPaths);
        
        for (let i = 0; i < svgPaths.length; i++) {
          const packSvgPath = svgPaths[i];
          const packTitle = svgTitles && svgTitles[i] ? svgTitles[i] : `${title} - Design ${i + 2}`;
          
          logger.log(`🎭 Adding pack design ${i + 2}:`, packSvgPath, packTitle);
          
          packStickers.push({
            listingId: `${listingId}-pack-${i + 2}`,
            title: packTitle,
            size: 'MICRO',
            color: 'Black',
            image: packSvgPath,
            etsyUrl,
            svgPath: packSvgPath,
            // Pass through pack data - these will be populated by SceneBuilder when it fetches unified data
            availableSizes: [],
            availableColors: [],
            dimensions: {},
            // 🎯 CRITICAL: Use the ACTUAL Etsy data fields that exist
            listing_data: null,
            inventory_data: null,
            category,
            isSet: false, // Individual stickers in pack are not sets
            setQuantity: 1,
            svgPaths: [],
            svgTitles: []
          });
        }
      }
      
      // Create pack data structure
      const packData = {
        listingId,
        title,
        isSetBatch: true,
        packId: `${title}-pack`,
        packStickers,
        packType,
        setQuantity,
        category,
        etsyUrl
      };
      
      logger.log('🎭 Created pack data:', packData);
      
      if (onAddToScene) {
        onAddToScene(packData);
      }
    } else {
      // Handle single sticker
      const stickerData = {
        listingId,
        title,
        size: 'MICRO',
        color: 'Black',
        image: svgPath || largestSizeImageUrl || firstImageUrl,
        etsyUrl
      };

      if (onAddToScene) {
        onAddToScene(stickerData);
      }
    }
  }, [listingId, title, svgPath, largestSizeImageUrl, firstImageUrl, etsyUrl, onAddToScene, isSet, svgPaths, svgTitles, category, packType, setQuantity]);

  // Intersection Observer - only load images when in viewport
  useEffect(() => {
    // Priority loads always load immediately
    if (isPriorityLoad) {
      setIsInView(true);
      return;
    }

    if (!cardRef.current) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect(); // Load once, then stop observing
        }
      },
      { rootMargin: '200px' } // Start loading 200px before entering viewport
    );
    
    observer.observe(cardRef.current);
    
    return () => observer.disconnect();
  }, [isPriorityLoad]);

  // When modal closes, check if card is now visible (for cards that mounted while modal was open)
  useEffect(() => {
    if (!isModalOpen && !imageLoaded && !isInView && cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight + 200 && rect.bottom > -200;
      if (isVisible) {
        setIsInView(true);
      }
    }
  }, [isModalOpen, imageLoaded, isInView]);

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Determine if image should load
  // Load when in view OR priority (no aggressive modal blocking - causes more problems than it solves)
  const shouldLoadImage = isInView || isPriorityLoad;

  return (
    <div className="homepage-sticker-card" onClick={handleClick} ref={cardRef}>
      {/* Image Container */}
      <div className="homepage-image-container">
        {/* Show loader when image is loading */}
        {!imageLoaded && !imageError && (firstImageUrl || largestSizeImageUrl || svgPath) && shouldLoadImage && (
          <div className="homepage-loader-container">
            <div className="homepage-gear-loader">
              <img 
                src="/assets/svgs/clothedEye-loader-spin.svg" 
                alt="" 
                className="homepage-gear-spin"
              />
              <img 
                src="/assets/svgs/clothedEye-loader-static.svg" 
                alt="" 
                className="homepage-gear-static"
              />
            </div>
          </div>
        )}
        
        {((shouldLoadImage || imageLoaded) && (firstImageUrl || largestSizeImageUrl || svgPath) && !imageError) ? (
          <img
            src={firstImageUrl || largestSizeImageUrl || svgPath}
            alt={title || 'Sticker'}
            className={`homepage-image ${imageLoaded ? 'loaded' : ''}`}
            loading={isPriorityLoad ? 'eager' : 'lazy'}
            fetchpriority={isPriorityLoad ? 'high' : 'auto'}
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{
              opacity: imageLoaded ? 1 : 0 // Hide image until loaded
            }}
          />
        ) : !imageLoaded && (firstImageUrl || largestSizeImageUrl || svgPath) && !imageError ? (
          <div className="homepage-placeholder">
            <img
              src={getCategoryFallbackImage()}
              alt={category ? `${category} Collection` : 'ClothedEye'}
              className="placeholder-logo"
            />
          </div>
        ) : (
          <div className="homepage-placeholder">
            <img
              src={getCategoryFallbackImage()}
              alt={category ? `${category} Collection` : 'ClothedEye'}
              className="placeholder-logo"
            />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="homepage-content">
        <h3 className="homepage-title">
          {(title || 'Sticker').substring(0, 20)}
          {(title || 'Sticker').length > 20 && '...'}
        </h3>
        {category && (
          <p className="homepage-category">{category}</p>
        )}
      </div>

      {/* Debug Overlay - Show sales/views numbers */}
      {showDebugOverlay && (
        <div className="debug-overlay">
          <div className="debug-number">
            {debugType === 'sales' ? (
              <>
                <span className="debug-label">SALES</span>
                <span className="debug-value">{totalSalesVolume || 0}</span>
              </>
            ) : (
              <>
                <span className="debug-label">VIEWS</span>
                <span className="debug-value">{views || 0}</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* TRUEscale Badge - Upper right corner */}
      {isTrueScale && (
        <div className="truescale-badge">
          <img 
            src="/assets/images/trueScale.png" 
            alt="TRUEscale" 
            className="truescale-logo"
          />
        </div>
      )}

    </div>
  );
});

HomepageStickerCard.propTypes = {
  listingId: PropTypes.string.isRequired,
  title: PropTypes.string,
  category: PropTypes.string,
  svgPath: PropTypes.string,
  firstImageUrl: PropTypes.string,
  largestSizeImageUrl: PropTypes.string,
  etsyUrl: PropTypes.string,
  onCardClick: PropTypes.func,
  onAddToScene: PropTypes.func,
  // Pack data
  svgPaths: PropTypes.arrayOf(PropTypes.string),
  svgTitles: PropTypes.arrayOf(PropTypes.string),
  isSet: PropTypes.bool,
  packType: PropTypes.string,
  setQuantity: PropTypes.number,
  // TRUEscale
  isTrueScale: PropTypes.bool
};

export default HomepageStickerCard;
